/* eslint react-hooks/rules-of-hooks: 0 */
import * as React from 'react'
import hoistNonReactStatics from 'hoist-non-react-statics'
import { parseISO, isValid, isAfter } from 'date-fns'
import axios from 'axios'

import * as store from '../utils/store'
import { useCountry } from './withCountry'
import AuthContext from './authContext'

export type Announcement = {
  id: string
  description: string
  title?: string
  endsAt?: string
  platform?: string
  region?: string
}

type ContextValue = {
  announcements: Array<Announcement>
  ignoreAnnouncementById: (id: string) => Promise<void>
}

const defaultContextValue: ContextValue = {
  announcements: [],
  ignoreAnnouncementById: async () => {},
}

export const AnnouncementsContext =
  React.createContext<ContextValue>(defaultContextValue)

type Props = Record<string, any>

export default function AnnouncementsContextHOCWrapper<
  T extends Record<string, any>,
>(WrappedComponent: React.ComponentType<T>) {
  function announcementsContextHOC(props: Props & T) {
    const [announcements, setAnnouncements] = React.useState<Announcement[]>([])
    const { country } = useCountry()
    const authContext = React.useContext(AuthContext)

    React.useEffect(() => {
      async function loadAnnouncements() {
        try {
          const { data: announcementsData } =
            await axios.get('/api/announcements')

          const ignoredAnnouncementIds = await store.get(
            'ignoredAnnouncementIds',
          )

          if (
            Array.isArray(announcementsData) &&
            announcementsData.length > 0
          ) {
            const nextAnnouncements = announcementsData.filter(
              (announcement: Announcement) => {
                if (!['ALL', 'WEB'].includes(announcement.platform || ''))
                  return false

                if (
                  !['ALL', country.toUpperCase()].includes(
                    announcement.region || '',
                  )
                )
                  return false

                // endsAt must be in ISO format https://en.wikipedia.org/wiki/ISO_8601
                // Example: 2019-12-26
                const endsAt =
                  typeof announcement.endsAt === 'string'
                    ? parseISO(announcement.endsAt)
                    : undefined

                if (endsAt && isValid(endsAt) && isAfter(new Date(), endsAt)) {
                  return false
                }

                if (
                  Array.isArray(ignoredAnnouncementIds) &&
                  ignoredAnnouncementIds.indexOf(announcement.id) !== -1
                ) {
                  return false
                }

                return true
              },
            )
            setAnnouncements(nextAnnouncements)
          }
        } catch (error) {
          console.log('Error loading announcements:', error)
          // Don't set announcements on error, keep empty array
        }
      }

      // Only load announcements when user is authenticated
      if (country && authContext.token) {
        loadAnnouncements()
      }
    }, [country, authContext.token])

    const ignoreAnnouncementById = React.useCallback(
      async (id: string) => {
        setAnnouncements(
          announcements.filter((announcement) => announcement.id !== id),
        )
        const ignoredAnnouncementIds = await store.get('ignoredAnnouncementIds')
        const nextIgnoredAnnouncementIds = Array.isArray(ignoredAnnouncementIds)
          ? [...ignoredAnnouncementIds, id]
          : [id]
        await store.set('ignoredAnnouncementIds', nextIgnoredAnnouncementIds)
      },
      [announcements],
    )

    const contextValue = React.useMemo(
      () => ({ announcements, ignoreAnnouncementById }),
      [announcements, ignoreAnnouncementById],
    )

    return (
      <AnnouncementsContext.Provider value={contextValue}>
        <WrappedComponent {...props} />
      </AnnouncementsContext.Provider>
    )
  }

  hoistNonReactStatics(announcementsContextHOC, WrappedComponent)
  return announcementsContextHOC
}

export function useAnnouncements(): [
  Announcement[],
  (id: string) => Promise<void>,
] {
  const { announcements, ignoreAnnouncementById } =
    React.useContext(AnnouncementsContext)

  return [announcements, ignoreAnnouncementById]
}
